// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { isDurationStep } from '@seedplanner/shared-utils';

interface TaskTimesReturn {
  readonly taskTimes: readonly { readonly name: string; readonly duration: number; readonly time: number }[];
  readonly harvestTime: number;
  readonly startTime: number;
}

export function getTaskTimesForCrop({
  totalGrowTime,
  bufferTime,
  orderDate,
  steps,
}: {
  readonly totalGrowTime: number;
  readonly bufferTime: number;
  readonly orderDate: number;
  readonly steps: readonly { readonly name: string; readonly time_to_start_after_first_step: number }[];
}): TaskTimesReturn {
  const harvestTime = orderDate - bufferTime;
  const startTime = harvestTime - totalGrowTime;
  const stepsToSort = [...steps];
  stepsToSort.sort(sortCropSteps);
  const taskTimes = addDurations(stepsToSort, totalGrowTime).map((s) => ({
    ...s,
    time: startTime + s.time_to_start_after_first_step,
  }));

  return {
    taskTimes,
    harvestTime,
    startTime,
  };
}

// Steps MUST BE SORTED!
export function addDurations<T extends { readonly time_to_start_after_first_step: number; readonly name: string }>(
  steps: readonly T[],
  totalGrowTime: number,
): (T & { duration: number })[] {
  const phases = steps.map((step, idx, arr) => {
    const nextDurationStep = arr.slice(idx + 1).find((s) => isDurationStep(s.name));
    const duration = nextDurationStep
      ? nextDurationStep.time_to_start_after_first_step - step.time_to_start_after_first_step
      : totalGrowTime - step.time_to_start_after_first_step;
    return { ...step, duration };
  });
  return phases;
}

interface CropStep {
  readonly name: string;
  readonly time_to_start_after_first_step: number;
}

export function startDateToOrderDate({
  startDate,
  totalGrowTime,
  bufferTime,
}: {
  readonly startDate: number;
  readonly totalGrowTime: number;
  readonly bufferTime: number;
}): number {
  return startDate + totalGrowTime + bufferTime;
}

export function orderDateToStartDate({
  orderDate,
  totalGrowTime,
  bufferTime,
}: {
  readonly orderDate: number;
  readonly totalGrowTime: number;
  readonly bufferTime: number;
}): number {
  return orderDate - bufferTime - totalGrowTime;
}

export function sortCropSteps(a: CropStep, b: CropStep) {
  // If one of the steps is the plant step, and it has the same time as another step, put plant step first
  if (a.time_to_start_after_first_step === b.time_to_start_after_first_step) {
    if (a.name.toLowerCase().includes('plant')) {
      return -1;
    }
    if (b.name.toLowerCase().includes('plant')) {
      return 1;
    }
  }

  return a.time_to_start_after_first_step - b.time_to_start_after_first_step;
}
